import React, { createRef, cloneElement } from 'react'
import classNames from 'classnames'

import type { LinkType, MenuType } from 'types'
import { LinkMenus } from '../Menus/HeaderMain/MobileMenu/Menu'

import BrandBar from '../Menus/BrandBar'
import DesktopMenu from '../Menus/HeaderMain/DesktopMenu'
import Logo, { DARK_LOGO, LIGHT_LOGO } from '../00-Primitives/Logo'
import MobileMenu from '../Menus/HeaderMain/MobileMenu'
import { useMediaQuery } from '../MediaQuery'

import style from './style.scss'

type IndexArray = Array<number>

type Props = {
  brandBarLabelText: string
  brandLinks: Array<MenuType>
  className?: string
  columnModalModule?: React.ReactElement
  datePicker: React.ReactElement
  desktopLinks: Array<LinkType>
  desktopLogoWidth: string
  hasDisabledAnimations?: boolean
  headerLabelText: string
  isLoggedIn?: boolean
  login: React.ReactElement
  logoLabelText: string
  mainMenuLabelText: string
  menuText: string
  mobileLinks: LinkMenus
  search?: React.ReactElement
  welcomeUserText?: string
}

type HeaderProps = Props & {
  belowTablet: boolean
}

type State = {
  columnModalExpanded: boolean
  headerHeight: number
  headerTransform: number
  mobileActiveIndexes: IndexArray
  mobileMenuExpanded: boolean
  scrollTopPosition: number
}

const COLUMN_MODAL_OFFSET = 45

class Header extends React.PureComponent<HeaderProps, State> {
  static defaultProps = {
    className: '',
    customDarkLogoURI: undefined,
    customLightLogoURI: undefined,
    hasCustomLogo: false,
    hasDisabledAnimations: false,
    shouldUseAnH1ForLogo: false,
  }

  state = {
    columnModalExpanded: false,
    headerHeight: 0,
    headerTransform: 0,
    mobileMenuExpanded: false,
    mobileActiveIndexes: [],
    scrollTopPosition: 0,
  }

  headerElement: { current: HTMLElement | null } = createRef()

  welcomeText: { current: HTMLDivElement | null } = createRef()

  componentDidMount() {
    window.addEventListener('resize', this.setHeaderHeight, { passive: true })
    setTimeout(this.setHeaderHeight, 0)
  }

  componentWillUnmount() {
    window.removeEventListener('scroll', this.setHeaderHeight)
  }

  componentDidUpdate(prevProps: Props) {
    // Reset header height if the user navigates off homepage or back to homepage
    if (this.props.columnModalModule !== prevProps.columnModalModule) {
      this.setHeaderHeight()
    }

    if (
      this.props.welcomeUserText !== prevProps.welcomeUserText &&
      this.props.isLoggedIn &&
      this.welcomeText.current
    ) {
      this.welcomeText.current.classList.add(style.welcomeTextVisible)

      setTimeout(() => {
        if (this.welcomeText.current) {
          this.welcomeText.current.classList.remove(style.welcomeTextVisible)
        }
      }, 3500)
    }
  }

  setHeaderHeight = () => {
    const { belowTablet, columnModalModule } = this.props
    if (this.headerElement.current) {
      const headerHeight =
        this.headerElement.current.clientHeight +
        (belowTablet && columnModalModule ? COLUMN_MODAL_OFFSET : 0)
      this.setState({ headerHeight })
    }
  }

  handleToggleExpanded = (menuToggle: boolean) =>
    this.setState(({ mobileMenuExpanded }) => ({
      mobileMenuExpanded: menuToggle ? !mobileMenuExpanded : false,
      mobileActiveIndexes: [],
    }))

  visibilitySelector = (belowTablet: boolean) => {
    const { mobileMenuExpanded } = this.state

    if (!belowTablet) {
      return LIGHT_LOGO
    }

    return mobileMenuExpanded ? DARK_LOGO : LIGHT_LOGO
  }

  handleColumnModalExpandCollapse = (val: boolean) => {
    this.setState({ columnModalExpanded: val })
  }

  render() {
    const {
      belowTablet,
      brandLinks,
      brandBarLabelText,
      className,
      columnModalModule,
      datePicker,
      desktopLogoWidth,
      desktopLinks,
      hasDisabledAnimations,
      headerLabelText,
      isLoggedIn,
      login,
      logoLabelText,
      mainMenuLabelText,
      menuText,
      mobileLinks,
      search,
      welcomeUserText,
    } = this.props

    const {
      columnModalExpanded,
      headerHeight,
      headerTransform,
      mobileMenuExpanded,
    } = this.state

    const logoProps = {
      ariaLabelText: logoLabelText,
      className: style.logo,
      desktopLogoWidth: Number(desktopLogoWidth),
    }

    return (
      <>
        {!columnModalExpanded && (
          <div
            style={
              { height: headerHeight } // Force height when fixed position on mobile.
            }
          />
        )}
        <header
          ref={this.headerElement}
          aria-label={headerLabelText}
          className={classNames(
            style.wrapper,
            {
              [style.expanded]: mobileMenuExpanded,
              [style.hidden]: columnModalExpanded,
              [style.disableAnimations]: hasDisabledAnimations,
            },
            className,
          )}
          style={
            belowTablet
              ? { transform: `translateY(${headerTransform}px)` }
              : undefined
          }
        >
          <div className={style.main}>
            <div className={style.leftColumn}>
              <Logo
                {...logoProps}
                visibleLogo={this.visibilitySelector(belowTablet)}
              />
            </div>
            {!belowTablet && (
              <div className={style.rightColumn}>
                <div className={style.brandBarLoginRow}>
                  <BrandBar aria-label={brandBarLabelText} links={brandLinks} />
                  <div ref={this.welcomeText} className={style.welcomeText}>
                    {login}
                  </div>
                </div>
                <div className={style.linkUtilsRow}>
                  <DesktopMenu
                    aria-label={mainMenuLabelText}
                    links={desktopLinks}
                  />
                  {cloneElement(datePicker, {
                    className: style.datePicker,
                  })}
                  {search}
                </div>
              </div>
            )}
            {belowTablet && (
              <>
                <span
                  className={classNames(style.utilRightMobile, {
                    [style.hidden]: mobileMenuExpanded,
                  })}
                >
                  {datePicker}
                  {search}
                </span>
                <MobileMenu
                  aria-label={mainMenuLabelText}
                  below={login}
                  className={style.mobileMenu}
                  expanded={mobileMenuExpanded}
                  linkMenus={mobileLinks}
                  menuText={menuText}
                  onToggleExpanded={this.handleToggleExpanded}
                />
              </>
            )}
          </div>
          {belowTablet && (
            <div className={style.bottomWrapper}>
              {columnModalModule &&
                cloneElement(columnModalModule, {
                  handleOpenCloseCallback: this.handleColumnModalExpandCollapse,
                })}
              {isLoggedIn && (
                <div ref={this.welcomeText} className={style.welcomeText}>
                  {welcomeUserText}
                </div>
              )}
            </div>
          )}
        </header>
      </>
    )
  }
}

// We require this hook in order to access prop values inside of the class methods
export default (props: Props) => {
  const { belowTablet } = useMediaQuery({
    tablet: true,
  })

  return <Header belowTablet={belowTablet} {...props} />
}
